import type { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import type {
    AddCooperationJobCommentPayload,
    CooperationJob,
    CooperationJobComment,
    CooperationJobConsumptionMaterialTypesResponse,
    CooperationJobResponse,
    CooperationJobsListResponse,
    CooperationJobStatus,
    CooperationJobStatusesResponse,
    UpdateCooperationJobPayload
} from '@/api/modules/cooperationJobsQueries'
import type { CooperantStockMaterialTypeDictItem } from '@/api/schemas/cooperantStocks/cooperantStocks'
import { cooperationJobsService } from '@/views/CooperationJobs/services/cooperationJobsService'

export const useCooperationJobsStore = defineStore('cooperationJobs', () => {
    const currentJob = ref<CooperationJob | null>(null)
    const statuses = ref<CooperationJobStatus[]>([])
    const consumptionMaterialTypes = ref<CooperantStockMaterialTypeDictItem[]>([])

    async function fetchCooperationJobs(queryString = ''): Promise<AxiosResponse<CooperationJobsListResponse>> {
        return cooperationJobsService.fetchCooperationJobs(queryString)
    }

    async function fetchCooperationJobStatuses(): Promise<AxiosResponse<CooperationJobStatusesResponse>> {
        const response = await cooperationJobsService.fetchCooperationJobStatuses()
        statuses.value = response.data.results
        return response
    }

    async function fetchConsumptionMaterialTypes(): Promise<AxiosResponse<CooperationJobConsumptionMaterialTypesResponse>> {
        const response = await cooperationJobsService.fetchConsumptionMaterialTypes()
        consumptionMaterialTypes.value = response.data.results
        return response
    }

    async function fetchCooperationJob(cooperationJobId: number | string): Promise<AxiosResponse<CooperationJob>> {
        const response = await cooperationJobsService.fetchCooperationJob(cooperationJobId)
        const normalizedResponse = normalizeCooperationJobResponse(response)
        currentJob.value = normalizedResponse.data
        return normalizedResponse
    }

    async function updateCooperationJob(
        cooperationJobId: number | string,
        payload: UpdateCooperationJobPayload
    ): Promise<AxiosResponse<CooperationJob>> {
        const response = await cooperationJobsService.updateCooperationJob(cooperationJobId, payload)
        const normalizedResponse = normalizeCooperationJobResponse(response)
        currentJob.value = normalizedResponse.data
        return normalizedResponse
    }

    async function addCooperationJobComment(
        payload: AddCooperationJobCommentPayload
    ): Promise<AxiosResponse<CooperationJobComment>> {
        const response = await cooperationJobsService.addCooperationJobComment(payload)

        if (currentJob.value?.id === payload.cooperationJobId) {
            currentJob.value.comments.unshift(response.data)
        }

        return response
    }

    async function refreshCooperationJobCommentsState(cooperationJobId: number | string): Promise<AxiosResponse<CooperationJob>> {
        const response = await cooperationJobsService.fetchCooperationJob(cooperationJobId)
        const normalizedResponse = normalizeCooperationJobResponse(response)

        if (currentJob.value?.id === Number(cooperationJobId)) {
            currentJob.value.canAddComment = normalizedResponse.data.canAddComment
            currentJob.value.comments = normalizedResponse.data.comments
        }

        return normalizedResponse
    }

    async function generateCooperationJobsReport(queryString: string, userEmail: string | null): Promise<AxiosResponse<unknown>> {
        return cooperationJobsService.generateCooperationJobsReport(queryString, userEmail)
    }

    async function deleteCooperationJob(cooperationJobId: number): Promise<AxiosResponse<void>> {
        const response = await cooperationJobsService.deleteCooperationJob(cooperationJobId)

        if (currentJob.value?.id === cooperationJobId) {
            currentJob.value = null
        }

        return response
    }

    async function printCooperationJobCards(cooperationJobId: number | string): Promise<AxiosResponse<ArrayBuffer>> {
        return cooperationJobsService.printCooperationJobCards(cooperationJobId)
    }

    return {
        currentJob,
        statuses,
        consumptionMaterialTypes,
        fetchCooperationJobs,
        fetchCooperationJobStatuses,
        fetchConsumptionMaterialTypes,
        fetchCooperationJob,
        updateCooperationJob,
        addCooperationJobComment,
        refreshCooperationJobCommentsState,
        generateCooperationJobsReport,
        deleteCooperationJob,
        printCooperationJobCards
    }
})

function normalizeCooperationJobResponse(response: AxiosResponse<CooperationJobResponse>): AxiosResponse<CooperationJob> {
    return {
        ...response,
        data: {
            ...response.data,
            canAddComment: response.data.canAddComment ?? false,
            comments: response.data.comments ?? []
        }
    }
}
